import React, { useEffect, useState } from "react";
import { X, ArrowRight, Check } from "lucide-react";

const SEEN_KEY = "ios_install_guide_seen";
export const isIosSafari = () => {
  const ua = navigator.userAgent;
  const ios = /iphone|ipad|ipod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return ios && /safari/i.test(ua) && !/crios|fxios|edgios|opios/i.test(ua);
};
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
export const openIosGuide = () => window.dispatchEvent(new Event("open-ios-guide"));

const Phone = ({ children }) => (
  <svg viewBox="0 0 200 300" className="w-44 h-64 mx-auto drop-shadow-xl" aria-hidden="true">
    <rect x="4" y="4" width="192" height="292" rx="28" fill="#0F2A1F" />
    <rect x="12" y="12" width="176" height="276" rx="22" fill="#F6F8F1" />
    <rect x="76" y="18" width="48" height="10" rx="5" fill="#0F2A1F" />
    {children}
  </svg>
);
const Pulse = ({ cx, cy }) => (
  <g><circle cx={cx} cy={cy} r="16" fill="#C5E063" opacity=".35"><animate attributeName="r" values="12;20;12" dur="1.6s" repeatCount="indefinite" /></circle>
    <circle cx={cx} cy={cy} r="11" fill="none" stroke="#8FA83A" strokeWidth="2.5" /></g>
);
const ShareIcon = ({ x, y, c = "#0F2A1F" }) => (
  <g stroke={c} strokeWidth="2" fill="none" strokeLinecap="round"><rect x={x - 6} y={y - 3} width="12" height="11" rx="2" /><path d={`M${x} ${y - 9} V${y + 3} M${x - 4} ${y - 5} L${x} ${y - 9} L${x + 4} ${y - 5}`} /></g>
);
const Page = () => (
  <g><rect x="24" y="40" width="152" height="18" rx="9" fill="#E3E9D6" /><text x="100" y="53" textAnchor="middle" fontSize="9" fill="#456">360immoinvest.com</text>
    <rect x="24" y="70" width="100" height="10" rx="4" fill="#0F2A1F" opacity=".8" /><rect x="24" y="88" width="152" height="40" rx="8" fill="#fff" />
    <rect x="24" y="136" width="72" height="40" rx="8" fill="#fff" /><rect x="104" y="136" width="72" height="40" rx="8" fill="#fff" /></g>
);

const Step1 = () => (
  <Phone><Page /><rect x="12" y="248" width="176" height="40" rx="0" fill="#EDF1E4" />
    {[40, 70, 130, 160].map((x) => <circle key={x} cx={x} cy="266" r="4" fill="#9AA79A" />)}<ShareIcon x={100} y={266} /><Pulse cx={100} cy={266} /></Phone>
);
const Step2 = () => (
  <Phone><Page /><rect x="12" y="120" width="176" height="168" rx="18" fill="#fff" />
    {["Copier", "Ajouter à la liste de lecture", "Sur l'écran d'accueil", "Ajouter aux favoris"].map((t, i) => (
      <g key={t}><rect x="22" y={138 + i * 34} width="156" height="28" rx="8" fill={i === 2 ? "#F1F7DD" : "#F6F8F1"} />
        <text x="32" y={156 + i * 34} fontSize="9" fontWeight={i === 2 ? 700 : 400} fill="#0F2A1F">{t}</text>
        {i === 2 && <g stroke="#0F2A1F" strokeWidth="1.8" fill="none"><rect x="158" y={145 + i * 34} width="12" height="12" rx="3" /><path d={`M164 ${148 + i * 34} v6 M161 ${151 + i * 34} h6`} /></g>}</g>
    ))}<Pulse cx={164} cy={219} /></Phone>
);
const Step3 = () => (
  <Phone><rect x="12" y="12" width="176" height="276" rx="22" fill="#F6F8F1" />
    <text x="30" y="58" fontSize="10" fill="#456">Annuler</text><text x="100" y="58" textAnchor="middle" fontSize="10" fontWeight="700" fill="#0F2A1F">Écran d'accueil</text>
    <text x="170" y="58" textAnchor="end" fontSize="10" fontWeight="700" fill="#2563EB">Ajouter</text>
    <rect x="24" y="76" width="152" height="54" rx="10" fill="#fff" /><rect x="34" y="84" width="38" height="38" rx="9" fill="#0F2A1F" />
    <text x="53" y="108" textAnchor="middle" fontSize="11" fontWeight="700" fill="#C5E063">360</text><text x="82" y="100" fontSize="10" fill="#0F2A1F">360immoinvest</text>
    <Pulse cx={160} cy={55} /></Phone>
);

const STEPS = [
  { Img: Step1, title: "Touchez le bouton Partager", text: "En bas de l'écran dans Safari (carré avec une flèche vers le haut)." },
  { Img: Step2, title: "Choisissez « Sur l'écran d'accueil »", text: "Faites défiler la liste vers le bas si vous ne le voyez pas tout de suite." },
  { Img: Step3, title: "Touchez « Ajouter »", text: "L'icône 360immoinvest apparaît sur votre écran d'accueil : ouvrez l'app d'un simple toucher." },
];

export const IosInstallGuide = () => {
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  useEffect(() => {
    const o = () => { setI(0); setOpen(true); };
    window.addEventListener("open-ios-guide", o);
    let t;
    if (isIosSafari() && !isStandalone() && !localStorage.getItem(SEEN_KEY)) t = setTimeout(o, 4000);
    return () => { window.removeEventListener("open-ios-guide", o); clearTimeout(t); };
  }, []);
  const close = () => { localStorage.setItem(SEEN_KEY, "1"); setOpen(false); };
  if (!open) return null;
  const S = STEPS[i];
  return (
    <div className="fixed inset-0 z-[80] bg-brand-greenDeep/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" data-testid="ios-install-guide">
      <div className="bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl px-6 pt-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] animate-in slide-in-from-bottom-4 fade-in duration-300">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-[0.2em] text-brand-limeDark font-semibold">Installer sur iPhone</p>
          <button onClick={close} className="text-brand-green/40 hover:text-brand-greenDeep" aria-label="Fermer" data-testid="ios-guide-close"><X className="w-5 h-5" /></button>
        </div>
        <div key={i} className="mt-4 animate-in fade-in slide-in-from-right-4 duration-300" data-testid={`ios-guide-step-${i + 1}`}>
          <S.Img />
          <p className="mt-5 text-xs text-brand-green/50">Étape {i + 1}/3</p>
          <h3 className="font-display text-2xl text-brand-greenDeep leading-tight">{S.title}</h3>
          <p className="text-sm text-brand-green/70 mt-1">{S.text}</p>
        </div>
        <div className="flex items-center justify-between mt-6">
          <div className="flex gap-1.5">{STEPS.map((_, k) => <button key={k} onClick={() => setI(k)} aria-label={`Étape ${k + 1}`} className={`h-2 rounded-full transition-[width,background-color] ${k === i ? "w-6 bg-brand-limeDark" : "w-2 bg-brand-soft"}`} data-testid={`ios-guide-dot-${k + 1}`} />)}</div>
          {i < 2 ? (
            <button onClick={() => setI(i + 1)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm" data-testid="ios-guide-next">Suivant <ArrowRight className="w-4 h-4" /></button>
          ) : (
            <button onClick={close} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-greenDeep text-white font-semibold text-sm" data-testid="ios-guide-done"><Check className="w-4 h-4" /> J'ai compris</button>
          )}
        </div>
      </div>
    </div>
  );
};
